import * as React from 'react';

import { m } from 'framer-motion';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { decryptData, encryptData } from 'src/utils/Crypto';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplaceAdminDialog from 'src/sections/product/view/replace-admin-dialog';
import AppVersion from 'src/components/AppVersion';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { isTenantDomain, getTenantSlug } from 'src/utils/domainContext';

// components
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import axiosInstance from 'src/api/axiosInstance';
import { RHFAutocomplete } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { useForm } from 'react-hook-form';
import { fetchUserRoles, logoutUser, refreshAvailableRoles } from 'src/redux/thunks/authThunks';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { isUserAdmin } from 'src/utils/roleUtils';

// Avatar color helper — must match theme/overrides/components/avatar.js
const colorByName = (name) => {
  const charAt = (name || '').charAt(0).toUpperCase();
  if (['A', 'B', 'C', 'F'].includes(charAt)) return 'primary';
  if (['D', 'E', 'G', 'H'].includes(charAt)) return 'secondary';
  if (['I', 'J', 'K', 'L'].includes(charAt)) return 'info';
  if (['M', 'N', 'O', 'P'].includes(charAt)) return 'success';
  if (['Q', 'R', 'S', 'T'].includes(charAt)) return 'warning';
  if (['U', 'V', 'W', 'X', 'Y', 'Z'].includes(charAt)) return 'error';
  return 'primary';
};

export default function AccountPopover() {
  // Can be null when user login or there is no profile photo
  const profilePhoto = useSelector((state) => state.user.profilePhoto);
  const organizationName = useSelector((state) => state.plan.orgFullName);
  const planName = useSelector((state) => state.plan.planName);
  const authSites = useSelector((state) => state.auth.sites);

  const router = useRouter();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const token = sessionStorage.getItem('token');
  const popover = usePopover();
  const [currentPlan, setCurrentPlan] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));
  const isAdmin = isUserAdmin(authSites);
  const decryptedEmail = user?.email ? decryptData(user.email) : '';
  const [selectedSite, setSelectedSite] = useState('');
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // State to hold profile picture URL
  const [profilePic, setProfilePic] = useState([]);

  const displayPlanName = useMemo(() => {
    const isTrial =
      currentPlan?.subscriptionStatus?.toUpperCase() === 'TRIAL' ||
      currentPlan?.paymentMode?.toUpperCase() === 'TRIAL' ||
      currentPlan?.isTrial === true ||
      currentPlan?.planCode?.toUpperCase() === 'FREE_TRIAL';

    if (isTrial) {
      return 'Free Trial';
    }

    const rawName = currentPlan?.planName || planName || '';
    const cleanName = rawName.replace(/\s+v\d+/i, '').trim();

    if (cleanName) {
      return cleanName;
    }

    const planCode = currentPlan?.planCode?.toUpperCase();
    if (planCode === 'PREMIUM') return 'Premium';
    if (planCode === 'STANDARD') return 'Standard';
    if (planCode === 'FREE') return 'Free';

    return 'Free Trial';
  }, [currentPlan, planName]);

  const [activeRoles, setActiveRoles] = useState([]);
  const [moreRolesAnchorEl, setMoreRolesAnchorEl] = useState(null);

  // Admin global replace state
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceTargetRole, setReplaceTargetRole] = useState(null);

  const handleOpenReplace = (roleName) => {
    // Find the original role object in globalSites
    const roleObj = globalSites?.find(
      (r) =>
        r.roleName === roleName &&
        (r.subscrSiteId === selectedSite?.subscrSiteId || r.subscrSiteName === selectedSite?.subscrSiteName)
    );
    if (roleObj) {
      setReplaceTargetRole({
        userSubscrSiteRoleId: roleObj.userSubscrSiteRoleId,
        subscrSiteName: roleObj.subscrSiteName,
      });
      setReplaceDialogOpen(true);
    }
  };

  const handleLogout = async () => {
    try {
      const resultAction = await dispatch(logoutUser());

      // ✅ Show success snackbar only if fulfilled
      if (logoutUser.fulfilled.match(resultAction)) {
        enqueueSnackbar('Logged out successfully', { variant: 'success' });
      }

      // ❌ Never show error snackbar on logout
      popover.onClose();

      // ✅ Always redirect
      router.replace(paths.auth.jwt.login);
    } catch (error) {
      console.error('Logout error:', error);

      // Even on unexpected errors, still logout client-side
      popover.onClose();
      router.replace(paths.auth.jwt.login);
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    router.push(path);
  };

  const fetchCurrentPlan = async () => {
    try {
      const response = await axiosInstance.get('/rest/dynamic-billing/current-plan', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCurrentPlan(response.data);

      return response.data;
    } catch (error) {
      console.error('CURRENT_PLAN_ERROR', error);

      return null;
    }
  };

  useEffect(
    () => {
      fetchCurrentPlan();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fetchData = async () => {
    try {
      const encryptedId = await fetchEncryptedToken(user.userId, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');
      const response = await axiosInstance.get(`/rest/users/getProfilePhoto/${encryptedId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setProfilePic(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sites, setSites] = useState([]);

  const defaultValues = useMemo(
    () => ({
      currentSites: sites || null,
    }),
    [sites]
  );

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const fetchSites = useCallback(
    async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
          console.error('User data not found in sessionStorage');
          return;
        }

        // Prepare payload after confirming userData exists
        const payload = { userId: encryptData(String(userData.userId)) };

        const availableRoles = JSON.parse(localStorage.getItem('availableRoles')) || [];

        setSites(availableRoles);

        const activeValidRoles = availableRoles.filter((role) => {
          if (!role.endDate) {
            return true;
          }

          return new Date(role.endDate) > new Date();
        });

        // Extract unique sites based on subscrSiteName
        const uniqueSites = activeValidRoles.reduce((accumulator, currentItem) => {
          const exists = accumulator.some(
            (site) => site?.subscrSiteName === currentItem?.subscrSiteName
          );
          if (!exists) accumulator.push(currentItem);
          return accumulator;
        }, []);

        // Sort alphabetically (case-insensitive)
        const sortedSites = uniqueSites.sort((siteA, siteB) => {
          const nameA = siteA.subscrSiteName.toLowerCase();
          const nameB = siteB.subscrSiteName.toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setSites(sortedSites);
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(
    () => {
      fetchSites();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    // Set the first site as default only when sites array changes
    if (sites.length > 0 && !selectedSite) {
      const defaultSite = JSON.parse(localStorage.getItem('selectedSubscrSite')) || sites[0];
      setSelectedSite(defaultSite); // Set the first site as default if no site is selected
      // Dispatch here immediately after setting
      dispatch(
        fetchUserRoles({
          userId: user.userId,
          siteId: decryptData(defaultSite.subscrSiteId),
          token: sessionStorage.getItem('token'),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedSite, sites]);

  useEffect(() => {
    setProfilePic(profilePhoto);
  }, [profilePhoto]);

  const globalSites = useSelector((state) => state.auth.sites);

  useEffect(() => {
    if (popover.open && selectedSite && globalSites && globalSites.length > 0) {
      const filtered = globalSites.filter((role) => {
        if (role.subscrSiteId !== selectedSite.subscrSiteId && role.subscrSiteName !== selectedSite.subscrSiteName) {
          return false;
        }
        if (role.endDate) {
          return new Date(role.endDate) > new Date();
        }
        return true;
      });
      const uniqueRoles = [...new Set(filtered.map(r => r.roleName))];
      // Ensure 'Admin' always appears first
      uniqueRoles.sort((a, b) => {
        if (a === 'Admin') return -1;
        if (b === 'Admin') return 1;
        return 0;
      });
      setActiveRoles(uniqueRoles);
      setMoreRolesAnchorEl(null);
    }
  }, [popover.open, selectedSite, globalSites]);

  // Function to handle site selection
  const handleSiteSelection = (site) => {
    setSelectedSite(site); // Update selectedSite when a different site is selected
    localStorage.setItem('selectedSubscrSite', JSON.stringify(site));
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          transition: 'all 0.2s ease',
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          }),
        }}
      >
        <Avatar
          src={profilePic || JSON.parse(localStorage.getItem('user'))?.userName}
          alt={JSON.parse(localStorage.getItem('user'))?.userName}
          sx={{
            width: 38,
            height: 38,
            fontWeight: 600,
            border: (theme) => `2px solid ${theme.palette.background.paper}`,
          }}
        />
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{
          width: 320,
          p: 0,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
        }}
      >
        {/* 👤 User Info */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: (theme) => {
              const avatarPalette = colorByName(JSON.parse(localStorage.getItem('user'))?.userName);
              return `linear-gradient(135deg, ${alpha(theme.palette[avatarPalette].light, 0.18)}, ${alpha(
                theme.palette[avatarPalette].main,
                0.08
              )})`;
            },
          }}
        >
          <Avatar
            src={profilePic || JSON.parse(localStorage.getItem('user'))?.userName}
            alt={JSON.parse(localStorage.getItem('user'))?.userName}
            sx={{
              width: 42,
              height: 42,
              fontWeight: 600,
              border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            {/* Username */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {JSON.parse(localStorage.getItem('user'))?.userName}
            </Typography>

            {/* Email */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: 'text.disabled',
                fontSize: '0.7rem',
              }}
              noWrap
            >
              {decryptedEmail}
            </Typography>

            {/* Organization Name */}
            {organizationName && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  mt: 0.4,
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Iconify
                  icon="solar:buildings-2-bold-duotone"
                  width={12}
                  sx={{ color: 'primary.main', flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    lineHeight: 1,
                  }}
                  noWrap
                >
                  {organizationName}
                </Typography>
              </Box>
            )}

            {/* Roles */}
            {activeRoles.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {activeRoles.slice(0, 2).map((role, idx) => (
                    <Chip
                      key={idx}
                      label={role}
                      size="small"
                      onDelete={
                        role === 'Admin'
                          ? (e) => {
                            e.stopPropagation();
                            handleOpenReplace(role);
                          }
                          : undefined
                      }
                      deleteIcon={
                        role === 'Admin'
                          ? (
                            <Tooltip title="Replace Admin Role" arrow placement="top">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  bgcolor: 'warning.main',
                                  color: 'warning.contrastText',
                                  ml: 0.5,
                                  mr: -0.5,
                                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.15)',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    bgcolor: 'warning.dark',
                                    transform: 'scale(1.15) rotate(180deg)'
                                  }
                                }}
                              >
                                <Iconify icon="mdi:account-convert" width={14} />
                              </Box>
                            </Tooltip>
                          )
                          : undefined
                      }
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        color: (theme) => theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.light,
                        border: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                          transform: 'translateY(-1px)',
                        }
                      }}
                    />
                  ))}
                  {activeRoles.length > 2 && (
                    <ButtonBase
                      onClick={(e) => {
                        e.stopPropagation();
                        setMoreRolesAnchorEl(e.currentTarget);
                      }}
                      sx={{
                        height: 22,
                        px: 1,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: moreRolesAnchorEl ? 'primary.main' : 'text.secondary',
                        bgcolor: (theme) => alpha(moreRolesAnchorEl ? theme.palette.primary.main : theme.palette.grey[500], 0.08),
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          bgcolor: (theme) => alpha(moreRolesAnchorEl ? theme.palette.primary.main : theme.palette.grey[500], 0.16),
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      +{activeRoles.length - 2} more
                    </ButtonBase>
                  )}
                </Box>
                <Popover
                  open={Boolean(moreRolesAnchorEl)}
                  anchorEl={moreRolesAnchorEl}
                  onClose={(e) => {
                    e.stopPropagation();
                    setMoreRolesAnchorEl(null);
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      p: 1.5,
                      mt: 1,
                      maxWidth: 240,
                      borderRadius: 1.5,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      background: (theme) => theme.palette.background.paper,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      maxHeight: 180,
                      overflowY: 'auto',
                      /* Custom Scrollbar */
                      '&::-webkit-scrollbar': {
                        width: '5px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.3),
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.5),
                      }
                    }
                  }}
                >
                  {activeRoles.slice(2).map((role, idx) => (
                    <Chip
                      key={idx + 2}
                      label={role}
                      size="small"
                      onDelete={
                        role === 'Admin'
                          ? (e) => {
                            e.stopPropagation();
                            handleOpenReplace(role);
                          }
                          : undefined
                      }
                      deleteIcon={
                        role === 'Admin'
                          ? (
                            <Tooltip title="Replace Admin Role" arrow placement="top">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  bgcolor: 'warning.main',
                                  color: 'warning.contrastText',
                                  ml: 0.5,
                                  mr: -0.5,
                                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.15)',
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    bgcolor: 'warning.dark',
                                    transform: 'scale(1.15) rotate(180deg)'
                                  }
                                }}
                              >
                                <Iconify icon="mdi:account-convert" width={14} />
                              </Box>
                            </Tooltip>
                          )
                          : undefined
                      }
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                        color: (theme) => theme.palette.mode === 'light' ? theme.palette.info.dark : theme.palette.info.light,
                        border: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.info.main, 0.16),
                          transform: 'translateY(-1px)',
                        }
                      }}
                    />
                  ))}
                </Popover>
              </Box>
            )}
          </Box>
        </Box>
        <Divider />

        {/* 🌐 Site Selector */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <FormProvider methods={methods}>
            <RHFAutocomplete
              name="currentSites"
              label="Current Site"
              placeholder="Select Site"
              options={sites}
              getOptionLabel={(option) => option?.subscrSiteName}
              value={selectedSite}
              onChange={(event, newValue) => handleSiteSelection(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option?.subscrSiteName}>
                  {option?.subscrSiteName || '-'}
                </li>
              )}
            />
          </FormProvider>
        </Box>



        {/* ⚙️ Profile Settings */}
        <MenuItem
          onClick={() => handleClickItem(paths.dashboard.user.account)}
          sx={{
            mx: 1,
            borderRadius: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Iconify icon="solar:settings-bold-duotone" width={20} sx={{ color: 'text.secondary', flexShrink: 0 }} />
          Profile Settings
        </MenuItem>

        {/* 🎫 My Tickets */}
        <MenuItem
          onClick={() => handleClickItem('/dashboard/support/tickets')}
          sx={{
            mx: 1,
            borderRadius: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Iconify icon="solar:ticket-sale-bold-duotone" width={20} sx={{ color: 'text.secondary', flexShrink: 0 }} />
          My Tickets
        </MenuItem>

        {isAdmin && (
          <MenuItem
            onClick={() => handleClickItem(paths.pricing)}
            sx={{
              mx: 1,
              my: 0.25,
              borderRadius: 1.5,
              py: 0.6,
              px: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Iconify
                icon="solar:crown-bold-duotone"
                width={17}
                sx={{ color: '#7b1fa2', flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                Plan:&nbsp;
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: '#1976d2',
                  }}
                >
                  {displayPlanName}
                </Box>
              </Typography>
            </Box>

            <Chip
              label="Upgrade Now"
              size="small"
              icon={<Iconify icon="solar:star-fall-minimalistic-bold-duotone" width={13} sx={{ color: '#fff !important' }} />}
              sx={{
                height: 22,
                fontSize: '0.675rem',
                fontWeight: 700,
                color: '#fff',
                background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
                borderRadius: 1,
                cursor: 'pointer',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(123, 31, 162, 0.25)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
                  boxShadow: '0 3px 8px rgba(123, 31, 162, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '& .MuiChip-icon': {
                  ml: 0.5,
                  mr: -0.25,
                },
                '& .MuiChip-label': {
                  pl: 0.25,
                  pr: 0.75,
                },
              }}
            />
          </MenuItem>
        )}

        {/* 🚪 Logout */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 1,
            mb: 0.5,
            borderRadius: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'error.main',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: alpha('#ff0000', 0.08),
            },
          }}
        >
          <Iconify icon="solar:logout-2-bold" width={20} sx={{ color: 'error.main', transform: 'rotate(180deg)', flexShrink: 0 }} />
          Logout
        </MenuItem>
        {/* App Version */}
        <Box
          sx={{
            px: 2,
            pb: 1.2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
              border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
              backdropFilter: 'blur(6px)',
            }}
          >
            <AppVersion size="small" variant="inline" showLabel />
          </Box>
        </Box>
      </CustomPopover>

      {replaceDialogOpen && (
        <ReplaceAdminDialog
          open={replaceDialogOpen}
          onClose={() => setReplaceDialogOpen(false)}
          currentUser={user}
          targetRole={replaceTargetRole}
          onSuccess={async () => {
            await dispatch(refreshAvailableRoles());
          }}
        />
      )}
    </>
  );
}
